import axios, {
  AxiosError,
  AxiosRequestHeaders,
  AxiosResponse,
  ResponseType,
} from "axios";
import message from "components/CustomMessage";
import _, { noop } from "lodash";
import makeCancellablePromise from "make-cancellable-promise";
import { ApiRequestInfo } from "types/anya";
import { getCsrfToken, resetUserAuthData } from "utils/authService";
import { setLocalStorage } from "utils/localStorageUtils";

const APIClient = axios.create();

const StatusCodeToIntercept = [401, 403, 404];

const isIframe = window.self !== window.top;

APIClient.interceptors.request.use((req) => {
  const { params } = req;
  if (!_.isEmpty(params)) {
    _.each(params, (value, key) => {
      if (_.isArray(value)) {
        params[key] = value.toString();
      }
    });
    if (_.isPlainObject(params)) {
      req.params = new URLSearchParams(
        // Filter both undefined and null
        _.omitBy(params, _.isNil),
      );
    } else {
      req.params = params;
    }
  }
  req.headers = {
    ...req.headers,
    "X-CSRF-TOKEN": getCsrfToken(),
  };
  return req;
});

APIClient.interceptors.response.use(
  (response) => response,
  (e) => {
    const status = e?.response?.status;
    if (StatusCodeToIntercept.includes(status)) {
      if (status === 401) {
        const hashLocation = window.location.hash.split(`#`)[1];
        //skip store last hash location for logout and login urls
        if (
          !hashLocation.toLowerCase().includes("logout") &&
          !hashLocation.toLowerCase().includes("login")
        ) {
          setLocalStorage("hashLocation", hashLocation);
        }
        resetUserAuthData();
        if (isIframe) {
          // for Azure extension on invalid credential it redirect to config page. To restrict this we have made this change.
          message.error("Invalid Credential");
        } else {
          window.location.hash = "#/logout";
        }
      }

      if (status === 403) {
        message.error(
          "You don't have permission to perform this action. Please contact your admin.",
        );
      }
      if (status === 404) {
        window.location.hash = "#/404";
      }
    }
    throw e;
  },
);

export default APIClient;

export async function failSafe(
  promise: Promise<AxiosResponse>,
  showDefaultMessage = true,
) {
  try {
    const response = await promise;
    return response.data;
  } catch (e) {
    if (axios.isCancel(e)) {
      // Ignore silently
      return;
    }
    console.dir(e);

    // show only AJAX erros here
    const error = e as AxiosError;
    if (error.isAxiosError) {
      const status = error.response?.status;
      // Check if response error is already handled by interceptor
      if (
        status &&
        !StatusCodeToIntercept.includes(status) &&
        showDefaultMessage
      ) {
        if (_.isEmpty(_.get(error, "response.data.errors"))) {
          showAPIError(error);
        }
      }
    }
    throw e;
  }
}

export const showAPIError = _.throttle(
  function showAPIError(e: AxiosError) {
    const messageLocation = [
      "response.data.message",
      "response.message",
      "data.message",
      "message",
    ];

    let errorMessage;
    messageLocation.every((loc) => {
      const m = _.result(e, loc);
      if (m) {
        errorMessage = m;
        return false;
      }
      return true;
    });
    const status = e.response?.status ?? 0;
    if (status === 504) {
      errorMessage = "Request Timed Out.";
    } else if (status >= 500) {
      errorMessage = "Something went wrong. Please try again!";
    }
    message.error(errorMessage);
  },
  1500,
  { trailing: false },
);

type HTTPMethod = "GET" | "POST" | "PUT" | "DELETE";

export type CancellableAPIRequest<T> = {
  promise: Promise<T>;
  cancel: () => void;
  /** Request information for API interception and caching (optional for backward compatibility) */
  requestInfo?: ApiRequestInfo;
};

export function makeCancellableAPIRequest<T>(
  method: HTTPMethod,
  url: string,
  options: {
    data?: any;
    params?: any;
    responseType?: ResponseType;
    headers?: AxiosRequestHeaders;
  } = {},
  showDefaultMessage = true,
): CancellableAPIRequest<T> {
  const abortController = new AbortController();
  const { promise, cancel: cancelPromise } = makeCancellablePromise(
    APIClient({
      method,
      url,
      data: options.data,
      params: options.params,
      signal: abortController.signal,
      responseType: options.responseType,
      headers: options.headers,
    }),
  );

  const handledPromise = failSafe(promise, showDefaultMessage);
  return {
    promise: handledPromise,
    cancel: () => {
      /**
       * Here, we are cancelling both API request and the promise.
       * If we only cancel the request, the promise still resolves with undefined and
       * throws error where the cases are not handled.
       * Cancelling the promise will prevent the promise from resolving as
       * we do not need that promise any more after cancelling it
       */
      cancelPromise();
      abortController.abort();
    },
    // Request info for API interception - used for generating unique cache keys for API
    requestInfo: {
      url,
      method,
      data: options.data,
      params: options.params,
    },
  };
}

export function getEmptyCancellableRequest<T>(data?: T) {
  return {
    promise: Promise.resolve(data) as Promise<T>,
    cancel: noop,
    requestInfo: {
      url: "",
      method: undefined,
      data: undefined,
      params: undefined,
    },
  };
}

export const EmptyCancellableRequest = {
  promise: Promise.resolve(),
  cancel: noop,
  requestInfo: {
    url: "",
    method: undefined,
    data: undefined,
    params: undefined,
  },
};
